import Vue from 'vue'
import Vuex from 'vuex'
import {
	auth,
	playlistsCollection,
	playlistsSongsCollection,
	queueCollection,
	songsCollection,
	variablesCollection,
	db
} from "./firebase";
import router from './router'
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { App } from "@capacitor/app";
import { KeepAwake } from "@capacitor-community/keep-awake";
import { Network } from "@capacitor/network";
import {StatusBar, Style} from "@capacitor/status-bar";
import { SongService } from "./services/SongService";
import { UserService } from "./services/UserService";
import md5 from "md5"
import {SessionService} from "@/services/SessionService";

KeepAwake.keepAwake();

const nightModeInitValue = localStorage.getItem("nightMode") === "true";

Vue.use(Vuex);

var songsUnsubscribe = null;
var playlistsUnsubscribe = null;
var queueUnsubscribe = null;
var playlistsSongsUnsubscribe = null;
var variablesUnsubscribe = null;
var sessionUnsubscribe = null;
var membershipUnsubscribe = null;

const store = new Vuex.Store({
	state: {
		device: null,
		sideOpened: true,
		sidePulled: 0,
		mainbarExtended: false,
		rootSideOpened: false,
		externalSearchOpened: false,
		localSearchOpened: false,
		songsFilter: {
			playlist: null,
			author: null,
			onlyQueue: false
		},
		session: null,
		songsFilterNonPlayed: false,
		songsSorting: false,
		playlists: {},
		songs: {},
		authors: {},
		externalSearchQuery: "",
		externalSongs: [],
		currentSong: null,
		currentUser: null,
		userSongs: [],
		queueSongs: [],
		queueSongsOrders: {},
		authorsSongs: {},
		playlistsSongs: {},
		songsPlaylists: {},
		playlistsSongsOrders: {},
		searchProcessing: false,
		nextQueueOrder: 1000,
		variables: {},
		settingsMenuOpened: false,
		networkOnline: null,
		loading: true,
		loadingText: null,
		detailFontSize: 1.1,
		detailHideChords: false,
		detailScrolling: false,
		datetimeMembershipExpire: null,
		isMembershipCancelled: null,
		nightMode: nightModeInitValue ? nightModeInitValue : false
	},
	mutations: {
		setSongsFilterNonPlayed(state, filter) {
			state.songsFilterNonPlayed = filter;
		},
		setSongsSorting(state, sorting) {
			state.songsSorting = sorting;
		},
		setDevice(state, device) {
			state.device = device;
		},
		setRootSideOpened(state, opened) {
			state.rootSideOpened = opened;
		},
		setMainbarExtended(state, extended) {
			state.mainbarExtended = extended;
		},
		setSettingsMenuOpened(state, opened) {
			state.settingsMenuOpened = opened;
		},
		setExternalSearchOpened(state, opened) {
			state.externalSearchOpened = opened;
		},
		setLocalSearchOpened(state, opened) {
			state.localSearchOpened = opened;
		},
		setSideOpened(state, opened) {
			state.sideOpened = opened;
		},
		setNightMode(state, nightMode) {
			state.nightMode = nightMode;
		},
		setSidePulled(state, pulled) {
			state.sidePulled = pulled;
		},
		setExternalSearchQuery(state, query) {
			state.externalSearchQuery = query;
		},
		setExternalSongs(state, externalSongs) {
			state.externalSongs = externalSongs;
		},
		addExternalSongs(state, externalSongs) {
			let buffIds = []
			state.externalSongs = [ ...state.externalSongs, ...externalSongs ].reduce((array, item) => {
				if (!buffIds.includes(item.id)) array.push(item)
				buffIds.push(item.id)
				return array
			}, [])
		},
		setCurrentSong(state, currentSong) {
			if (!("songId" in currentSong)) {
				currentSong.songId = currentSong.id;
			}
			state.currentSong = currentSong;
		},
		setCurrentSongTranspose(state, transpose) {
			Vue.set(state.currentSong, "transpose", transpose);
		},
		setCurrentSongDuration(state, duration) {
			Vue.set(state.currentSong, "duration", duration);
		},
		setCurrentSongContent(state, content) {
			Vue.set(state.currentSong, "content", content);
		},
		setCurrentSongTitle(state, title) {
			Vue.set(state.currentSong, "title", title);
		},
		setCurrentSongAuthor(state, author) {
			Vue.set(state.currentSong, "author", author);
			Vue.set(state.currentSong, "authorId", md5(author));
		},
		setCurrentUser(state, currentUser) {
			state.currentUser = currentUser;
		},
		setSearchProcessing(state, searchProcessing) {
			state.searchProcessing = searchProcessing;
		},
		setNetworkOnline(state, networkOnline) {
			state.networkOnline = networkOnline;
		},
		updateAuthor(state, author) {
			Vue.set(state.authors, author.id, author);
		},

		clearSongs(state) {
			state.songs = {};
		},
		updateSong(state, song) {
			Vue.set(state.songs, song.songId, song);
		},
		removeSong(state, song) {
			Vue.delete(state.songs, song.songId);
		},

		updatePlaylist(state, playlist) {
			Vue.set(state.playlists, playlist.id, playlist);
			if (!(playlist.id in state.playlistsSongs)) {
				state.playlistsSongs[playlist.id] = [];
			}
		},
		removePlaylist(state, playlistId) {
			Vue.delete(state.playlists, playlistId);
		},
		setVariable(state, variable) {
			Vue.set(state.variables, variable.key, variable.value);
		},
		setLoading(state, loading) {
			state.loading = loading;
			if (!loading) {
				state.loadingText = null;
			}
		},
		setLoadingText(state, loadingText) {
			state.loadingText = loadingText;
		},
		updateQueueSong(state, [ songId, order ]) {
			if (!state.queueSongs.includes(songId)) {
				state.queueSongs.push(songId);
			}

			Vue.set(state.queueSongsOrders, songId, order);
		},
		removeQueueSong(state, songId) {
			state.queueSongs = state.queueSongs.filter(i => songId !== i);
			Vue.delete(state.queueSongsOrders, songId);
		},
		setSongsFilter(state, songsFilter) {
			state.songsFilter = songsFilter;
		},
		setSession(state, session) {
			state.session = session;
		},
		setDatetimeMembershipExpire(state, datetime) {
			state.datetimeMembershipExpire = datetime;
		},
		setIsMembershipCancelled(state, isMembershipCancelled) {
			state.isMembershipCancelled = isMembershipCancelled;
		},
		addAuthorSong(state, [ authorId, songId ]) {
			if (!(authorId in state.authorsSongs)) {
				state.authorsSongs[authorId] = [];
			}

			if (!state.authorsSongs[authorId].includes(songId)) {
				state.authorsSongs[authorId].push(songId);
			}
		},
		setNextQueueOrder(state, order) {
			state.nextQueueOrder = order;
		},
		updatePlaylistSong(state, playlistSong) {
			if (!(playlistSong.playlistId in state.playlistsSongs)) {
				state.playlistsSongs[playlistSong.playlistId] = [];
			}
			if (!state.playlistsSongs[playlistSong.playlistId].includes(playlistSong.songId)) {
				state.playlistsSongs[playlistSong.playlistId].push(playlistSong.songId);
			}

			if (!(playlistSong.songId in state.songsPlaylists)) {
				state.songsPlaylists[playlistSong.songId] = [];
			}
			if (!state.songsPlaylists[playlistSong.songId].includes(playlistSong.playlistId)) {
				state.songsPlaylists[playlistSong.songId].push(playlistSong.playlistId);
			}

			if (!(playlistSong.playlistId in state.playlistsSongsOrders)) {
				state.playlistsSongsOrders[playlistSong.playlistId] = {};
			}
			Vue.set(state.playlistsSongsOrders[playlistSong.playlistId], playlistSong.songId, playlistSong.order);

			document.dispatchEvent(new Event("RefreshSongs"));

		},
		removePlaylistSong(state, playlistSong) {
			state.playlistsSongs[playlistSong.playlistId] = state.playlistsSongs[playlistSong.playlistId]
				.filter(id => id !== playlistSong.songId);

			state.songsPlaylists[playlistSong.songId] = state.songsPlaylists[playlistSong.songId]
				.filter(id => id !== playlistSong.playlistId);

			Vue.delete(state.playlistsSongsOrders[playlistSong.playlistId], playlistSong.songId);

			document.dispatchEvent(new Event("RefreshSongs"));
		},
		setDetailFontSize(state, fontSize) {
			state.detailFontSize = fontSize;
		},
		setDetailHideChords(state, hideChords) {
			state.detailHideChords = hideChords;
		},
		setDetailScrolling(state, scrolling) {
			state.detailScrolling = scrolling;
		},
	},
	actions: {
	},
});

Device.getInfo().then(async device => {
	const app = await (() => new Promise(async resolve => {
		try { resolve(await App.getInfo()) }
		catch (e) { resolve({}) }
	}))()

	device = { ...device, ...app, ...await Device.getId() }

	if (window.todesktop) {
		if (navigator.platform.indexOf('Mac') > -1) {
			device.platform = "desktop-mac";
		} else {
			device.platform = "desktop-win";
		}
	}

	if ("model" in device && device.model !== "iPad" && Capacitor.isPluginAvailable("StatusBar")) {
		StatusBar.hide();
	} else {
		StatusBar.setStyle({ style: Style.Dark })
	}

	const languageCode = await Device.getLanguageCode();
	let language = (languageCode && (languageCode.value === "cs" || languageCode.value === "cs-CZ")) ? "cs" : "en";
	if (localStorage.getItem("language") === undefined || localStorage.getItem("language") === null) {
		router.app._i18n.locale = language;
	} else {
		language = localStorage.getItem("language") === "cs" ? "cs" : "en";
	}

	store.commit("setDevice", { ...device, language });

	if (device.platform === "web" && !/localhost/.test(window.location) && router.currentRoute.name !== "export-song" && router.currentRoute.name !== "session") {
		if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) {
			window.location = "https://itunes.apple.com/us/app/mango-zpěvn%C3%ADk/id1462046181";
		} else if (/(android)/i.test(navigator.userAgent)) {
			window.location = "https://play.google.com/store/apps/details?id=com.motvicka.mangosongbook";
		}
	}
});

auth.onAuthStateChanged((user) => {
	store.commit("setCurrentUser", user);

	if (user && store.state.networkOnline) {
		setTimeout(() => {
			UserService.saveUserInfo()
		}, 1000);
	}

	store.commit("clearSongs");
	if (songsUnsubscribe !== null) songsUnsubscribe();
	if (playlistsUnsubscribe !== null) playlistsUnsubscribe();
	if (playlistsSongsUnsubscribe !== null) playlistsSongsUnsubscribe();
	if (queueUnsubscribe !== null) queueUnsubscribe();
	if (variablesUnsubscribe !== null) variablesUnsubscribe();
	if (sessionUnsubscribe !== null) sessionUnsubscribe();
	if (membershipUnsubscribe !== null) membershipUnsubscribe();

	if (user) {
		document.dispatchEvent(new Event("LoggedIn"));
		songsUnsubscribe = songsCollection.where("userId", "==", user.uid).onSnapshot((snapshot) => {
			snapshot.docChanges().forEach((change) => {
				if (change.type === "added" || change.type === "modified") {
					store.commit("updateSong", { id: change.doc.id, ...change.doc.data() });
					store.commit("updateAuthor", { id: change.doc.data().authorId, title: change.doc.data().author });
					store.commit("addAuthorSong", [ change.doc.data().authorId, change.doc.data().songId ]);

					if (store.state.currentSong === null && "song" in router.app._route.params && router.app._route.params.song === change.doc.data().songId) {
						store.commit("setCurrentSong", { id: change.doc.id, ...change.doc.data() });
					}

					if (router.currentRoute.name === "zpevnik" || router.currentRoute.name === "zpevnik-song") {
						store.commit("setLoading", false);
					}
				}
				if (change.type === "removed") {
					store.commit("removeSong", change.doc.data());
				}
			});
		});

		playlistsUnsubscribe = playlistsCollection.where("userId", "==", user.uid).onSnapshot((snapshot) => {
			snapshot.docChanges().forEach((change) => {
				if (change.type === "added" || change.type === "modified") {
					store.commit("updatePlaylist", { id: change.doc.id, ...change.doc.data() });
				}
				if (change.type === "removed") {
					store.commit("removePlaylist", change.doc.id);
				}
			});
		});

		playlistsSongsUnsubscribe = playlistsSongsCollection.where("userId", "==", user.uid).orderBy("order").onSnapshot((snapshot) => {
			snapshot.docChanges().forEach((change) => {
				if (change.type === "added" || change.type === "modified") {
					store.commit("updatePlaylistSong", change.doc.data());
				}
				if (change.type === "removed") {
					store.commit("removePlaylistSong", change.doc.data());
				}
			});
		});

		queueUnsubscribe = queueCollection.where("userId", "==", user.uid).orderBy("order").onSnapshot(snapshot => {
			snapshot.docChanges().forEach((change) => {
				if (change.type === "added" || change.type === "modified") {
					store.commit("updateQueueSong", [ change.doc.data().songId, change.doc.data().order ]);
					if (change.doc.data().order >= store.state.nextQueueOrder) {
						store.commit("setNextQueueOrder", change.doc.data().order + 1000);
					}
				}
				if (change.type === "removed") {
					store.commit("removeQueueSong", change.doc.data().songId);
				}
			});
		});

		variablesUnsubscribe = variablesCollection.onSnapshot(snapshot => {
			let checkUpdate = false;
			snapshot.docChanges().forEach((change) => {
				if (change.type === "added" || change.type === "modified") {
					store.commit("setVariable", { key: change.doc.id, value: change.doc.data().value });
				}

				if (change.type === "added" && change.doc.id.includes("Version")) {
					checkUpdate = true;
				}
			});

			if (checkUpdate) {
				setTimeout(() => { document.dispatchEvent(new Event("CheckUpdate")); }, 100);
			}
		});

		sessionUnsubscribe = db.collection("sessions").doc(user.uid).onSnapshot(snapshot => {
			if (snapshot.exists) {
				const session = {
					name: snapshot.data().sessionName,
					datetimeExpire: snapshot.data().sessionDatetimeExpire.toDate(),
				};

				if (session.datetimeExpire > new Date()) {
					store.commit("setSession", session);
				} else {
					store.commit("setSession", null);
					SessionService.removeSession();
				}

			} else {
				store.commit("setSession", null);
			}
		});

		membershipUnsubscribe = db.collection("memberships").doc(user.uid).onSnapshot(snapshot => {
			store.commit(
				"setDatetimeMembershipExpire",
				snapshot.exists ? snapshot.data().datetimeExpire.toDate() : null
			);
			store.commit(
				"setIsMembershipCancelled",
				snapshot.exists
					? ("cancelled" in snapshot.data() && snapshot.data().cancelled)
					: false
			);
		});
	} else {
		if (router.currentRoute.name !== "export-song" && router.currentRoute.name !== "session") {
			setTimeout(() => {
				// nastavuji s intervalem, protože po odhlaseni jinak dojde k vytvoreni noveho uzivatele,
				// okamzitemu reloadu (protoze odhlaseni) a pak vytvoreni noveho uzivatele.
				auth.signInAnonymously().then(() => {
					SongService.prepareIntroSongs();
					document.dispatchEvent(new Event("LoggedIn"));
				}).catch(() => { });
			}, 500);
		}
	}
});

Network.getStatus().then(status => {
	store.commit("setNetworkOnline", status.connected);
}).catch(() => {
	// z webu to hodi chybu, a tam pocitame, ze je online
	store.commit("setNetworkOnline", true);
});

window.addEventListener("online",  () => {
	store.commit("setNetworkOnline", true);
	Vue.notify({
		type: "success",
		text: "Jsi online.",
	});
});
window.addEventListener("offline", () => {
	store.commit("setNetworkOnline", false);
	Vue.notify({
		type: "error",
		text: "Internetové připojení ztraceno.",
	});
});

document.addEventListener("DOMContentLoaded", function() {
	setTimeout(() => {
		if (router.currentRoute.name === "zpevnik" || router.currentRoute.name === "zpevnik-song") {
			store.commit("setLoading", false);
		}
	}, 2000);
});

export default store;
